import { useState, useEffect } from "react";
import { Link, useLocation, useNavigate } from "react-router-dom";
import { Menu, X, User, LogOut, KeyRound, ExternalLink } from "lucide-react";
import { useTranslation } from "react-i18next";
import { useAuth } from "@/context/AuthContext";
import { track } from "@/lib/analytics";
import { ModeSwitcher } from "@/components/ModeSwitcher";
import { LanguageToggle } from "@/components/LanguageToggle";

export default function Navbar() {
  const [open, setOpen] = useState(false);
  const [scrolled, setScrolled] = useState(false);
  const location = useLocation();
  const navigate = useNavigate();
  const { t } = useTranslation();
  const { user, logout } = useAuth();
  const isCustomer = user && user.role === "customer";

  const links = [
    { to: "/", key: "home", label: t("nav.home") },
    { to: "/fleet", key: "fleet", label: t("nav.fleet") },
    { to: "/become-a-host", key: "becomehost", label: t("nav.becomeHost", "Become a Host") },
    { to: "/contact", key: "contact", label: t("nav.contact") },
  ];

  useEffect(() => {
    const onScroll = () => setScrolled(window.scrollY > 20);
    window.addEventListener("scroll", onScroll);
    return () => window.removeEventListener("scroll", onScroll);
  }, []);

  useEffect(() => setOpen(false), [location.pathname]);

  return (
    <header
      data-testid="navbar"
      className={`fixed top-0 left-0 right-0 z-50 transition-all duration-300 ${
        scrolled ? "bg-white/85 backdrop-blur-xl border-b border-slate-100 shadow-sm" : "bg-transparent"
      }`}
    >
      <div className="max-w-7xl mx-auto px-5 sm:px-8 h-20 flex items-center justify-between">
        <Link to="/" data-testid="nav-logo" aria-label="Belo Rentals home" className="flex items-center group">
          <img
            src={`${process.env.PUBLIC_URL}/belo-wordmark.png`}
            alt="Belo Rentals — car rental near Logan Airport, Boston"
            className="h-7 sm:h-8 w-auto transition-transform group-hover:-translate-y-0.5"
          />
        </Link>

        <nav className="hidden md:flex items-center gap-9">
          {links.map((l) => (
            <Link
              key={l.to}
              to={l.to}
              data-testid={`nav-${l.key}`}
              onClick={() => l.key === "becomehost" && track("become_host_nav_click", { source: "desktop" })}
              className={`text-sm font-medium transition-colors ${
                location.pathname === l.to ? "text-blue-600" : "text-slate-600 hover:text-slate-900"
              }`}
            >
              {l.label}
            </Link>
          ))}
          <a
            href="https://belomotors.com"
            target="_blank"
            rel="noopener noreferrer"
            data-testid="nav-sales"
            className="text-sm font-medium text-slate-600 hover:text-slate-900 transition-colors inline-flex items-center gap-1"
          >
            Sales
            <ExternalLink size={13} className="opacity-60" />
          </a>
        </nav>

        <div className="hidden md:flex items-center gap-3">
          <LanguageToggle />
          {isCustomer ? (
            <>
              {user.is_host ? (
                <ModeSwitcher />
              ) : (
                <Link to="/host" data-testid="nav-host" className="flex items-center gap-1.5 text-sm font-semibold text-blue-600 hover:text-blue-700 transition-colors">
                  <KeyRound size={15} /> {t("nav.hostYourCar")}
                </Link>
              )}
              <Link to="/account" data-testid="nav-account" className={`flex items-center gap-2 text-sm font-medium transition-colors ${location.pathname === "/account" ? "text-blue-600" : "text-slate-600 hover:text-slate-900"}`}>
                <User size={16} /> {user.name?.split(" ")[0] || t("nav.account")}
              </Link>
              <button data-testid="nav-logout" onClick={async () => { await logout(); navigate("/"); }} className="text-slate-400 hover:text-slate-700 transition-colors" title={t("nav.signOut")}><LogOut size={18} /></button>
            </>
          ) : (
            <Link to="/login" data-testid="nav-signin" className="text-sm font-medium text-slate-600 hover:text-slate-900 transition-colors">{t("nav.signIn")}</Link>
          )}
          <button
            data-testid="nav-book-now"
            onClick={() => navigate("/fleet")}
            className="bg-blue-600 text-white px-6 py-2.5 rounded-full text-sm font-semibold hover:bg-blue-700 transition-all hover:-translate-y-0.5 shadow-sm"
          >
            {t("nav.bookNow")}
          </button>
        </div>

        <div className="md:hidden flex items-center gap-3">
          <LanguageToggle />
          <button data-testid="nav-mobile-toggle" className="text-slate-900" onClick={() => setOpen(!open)}>
            {open ? <X size={24} /> : <Menu size={24} />}
          </button>
        </div>
      </div>

      {open && (
        <div className="md:hidden bg-white border-t border-slate-100 px-5 py-6 flex flex-col gap-4 shadow-lg">
          {links.map((l) => (
            <Link key={l.to} to={l.to} data-testid={`nav-mobile-${l.key}`} onClick={() => l.key === "becomehost" && track("become_host_nav_click", { source: "mobile" })} className="text-slate-700 font-medium">
              {l.label}
            </Link>
          ))}
          <a
            href="https://belomotors.com"
            target="_blank"
            rel="noopener noreferrer"
            data-testid="nav-mobile-sales"
            className="text-slate-700 font-medium inline-flex items-center gap-1.5"
          >
            Sales <ExternalLink size={14} className="opacity-60" />
          </a>
            <>
              {user.is_host ? (
                <div className="py-1"><ModeSwitcher size="lg" /></div>
              ) : (
                <Link to="/host" data-testid="nav-mobile-host" className="text-blue-600 font-semibold flex items-center gap-2"><KeyRound size={16} /> {t("nav.hostYourCar")}</Link>
              )}
              <Link to="/account" data-testid="nav-mobile-account" className="text-slate-700 font-medium flex items-center gap-2"><User size={16} /> {t("nav.myTrips")}</Link>
              <button data-testid="nav-mobile-logout" onClick={async () => { await logout(); navigate("/"); }} className="text-left text-slate-500 font-medium flex items-center gap-2"><LogOut size={16} /> {t("nav.signOut")}</button>
            </>
          ) : (
            <Link to="/login" data-testid="nav-mobile-signin" className="text-slate-700 font-medium">{t("nav.signIn")}</Link>
          )}
          <button data-testid="nav-mobile-book" onClick={() => navigate("/fleet")} className="bg-blue-600 text-white px-6 py-3 rounded-full font-semibold">
            {t("nav.bookNow")}
          </button>
        </div>
      )}
    </header>
  );
}
